<template>
  <div class="contact-selection-list">
    <!-- Header -->
    <div class="contact-header">
      <h3>Select Recipients</h3>
      <div class="contact-stats">
        <span class="selected-count">{{ selectedContacts.length }} selected</span>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="contact-search">
      <div class="search-input-wrapper">
        <font-awesome-icon :icon="['fa', 'search']" class="search-icon" />
        <input 
          v-model="searchTerm"
          @input="filterContacts"
          type="text" 
          placeholder="Search contacts..."
          class="search-input"
        />
        <button 
          v-if="searchTerm" 
          @click="clearSearch" 
          class="clear-search"
        >
          <font-awesome-icon :icon="['fa', 'times']" />
        </button>
      </div>
    </div>



    <!-- Contact Categories -->
    <div class="contact-categories">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <font-awesome-icon :icon="['fa', 'spinner']" class="fa-spin" />
        <span>Loading contacts...</span>
      </div>

      <!-- Contact Lists -->
      <div v-else class="contact-lists">
        <!-- Assigned Team Members (shown first when viewing a patient chart) -->
        <div 
          v-if="filteredContacts.assignedTeamMembers && filteredContacts.assignedTeamMembers.length > 0" 
          class="contact-category"
        >
          <div class="category-header" @click="toggleCategory('assignedTeamMembers')">
            <font-awesome-icon 
              :icon="['fas', categoryExpanded.assignedTeamMembers ? 'caret-down' : 'caret-right']" 
              class="expand-icon"
            />
            <font-awesome-icon :icon="['fas', 'users']" class="category-icon" />
            <span class="category-title">Assigned Patients</span>
            <span class="category-count">({{ filteredContacts.assignedTeamMembers.length }})</span>
          </div>
          <div v-show="categoryExpanded.assignedTeamMembers" class="contact-list">
            <div 
              v-for="contact in filteredContacts.assignedTeamMembers" 
              :key="contact.id"
              class="contact-item"
            >
              <!-- Main contact row -->
              <div class="contact-main-row">
                <!-- Checkbox for selection -->
                <div class="contact-checkbox" @click="toggleContact(contact)">
                  <span v-if="isContactSelected(contact)" class="checked-text">✓</span>
                  <div 
                    v-else
                    class="unchecked-box"
                  ></div>
                </div>
                
                <!-- Contact info -->
                <div class="contact-info">
                  <div class="contact-avatar">
                    <font-awesome-icon :icon="getContactIcon(contact.accountType)" />
                  </div>
                  <div class="contact-details">
                    <div class="contact-name">{{ contact.name }}</div>
                    <div class="contact-role">{{ getDisplayRole(contact.accountType) }}</div>
                  </div>
                </div>

                <div class="contact-status">
                  <button
                    v-if="isPatientContact(contact)"
                    type="button"
                    class="group-indicator-btn"
                    @click.stop="toggleContactGroups(contact)"
                  >
                    <font-awesome-icon
                      :icon="['fas', showContactGroups[contact.id] ? 'caret-down' : 'caret-right']"
                      class="team-caret-icon"
                    />
                  </button>
                </div>
              </div>

              <div v-if="isPatientContact(contact) && showContactGroups[contact.id]" class="contact-groups-dropdown">
                <div class="team-panel-header">
                  <button type="button" class="add-team-btn" @click.stop="addPatientTeam(contact)">
                    Add Entire Team
                  </button>
                  <span class="team-count">
                    {{ getPatientTeamMembers(contact).length }} team member{{ getPatientTeamMembers(contact).length !== 1 ? 's' : '' }}
                  </span>
                </div>
                <div v-if="getPatientTeamMembers(contact).length === 0" class="team-empty">
                  No team members found for this patient.
                </div>
                <div v-else class="groups-list">
                  <div
                    v-for="member in getPatientTeamMembers(contact)"
                    :key="member.id || member.email || member.name"
                    class="group-option"
                  >
                    <div class="group-info">
                      <div class="group-name">{{ member.name }}</div>
                      <div class="group-details">
                        <span class="patient-name">{{ member.groupName }}</span>
                      </div>
                    </div>
                    <button type="button" class="team-add-single-btn" @click.stop="toggleContact(member)">
                      {{ isContactSelected(member) ? 'Added' : 'Add' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Administrators/Supervisors (if user can message them) -->
        <div 
          v-if="filteredContacts.administrators.length > 0" 
          class="contact-category"
        >
          <div class="category-header" @click="toggleCategory('administrators')">
            <font-awesome-icon 
              :icon="['fas', categoryExpanded.administrators ? 'caret-down' : 'caret-right']" 
              class="expand-icon"
            />
            <font-awesome-icon :icon="['fas', 'user-md']" class="category-icon" />
            <span class="category-title">Administrators</span>
            <span class="category-count">({{ filteredContacts.administrators.length }})</span>
          </div>
          <div v-show="categoryExpanded.administrators" class="contact-list">
            <div 
              v-for="contact in filteredContacts.administrators" 
              :key="contact.id"
              class="contact-item"
            >
              <!-- Main contact row -->
              <div class="contact-main-row">
                <!-- Checkbox for selection -->
                <div class="contact-checkbox" @click="toggleContact(contact)">
                  <span v-if="isContactSelected(contact)" class="checked-text">✓</span>
                  <div 
                    v-else
                    class="unchecked-box"
                  ></div>
                </div>
                
                <!-- Contact info -->
                <div class="contact-info">
                  <div class="contact-avatar">
                    <font-awesome-icon :icon="['fas', 'envelope']" />
                  </div>
                  <div class="contact-details">
                    <div class="contact-name">{{ contact.name }}</div>
                    <div class="contact-role">{{ contact.email }}</div>
                  </div>
                </div>
                
                <!-- Status or Group Indicator -->
                <div class="contact-status">
                  <button
                    v-if="isPatientContact(contact)"
                    type="button"
                    class="group-indicator-btn"
                    @click.stop="toggleContactGroups(contact)"
                  >
                    <font-awesome-icon
                      :icon="['fas', showContactGroups[contact.id] ? 'caret-down' : 'caret-right']"
                      class="team-caret-icon"
                    />
                  </button>
                </div>
              </div>
              
              <div v-if="isPatientContact(contact) && showContactGroups[contact.id]" class="contact-groups-dropdown">
                <div class="team-panel-header">
                  <button type="button" class="add-team-btn" @click.stop="addPatientTeam(contact)">
                    Add Entire Team
                  </button>
                  <span class="team-count">
                    {{ getPatientTeamMembers(contact).length }} team member{{ getPatientTeamMembers(contact).length !== 1 ? 's' : '' }}
                  </span>
                </div>
                <div v-if="getPatientTeamMembers(contact).length === 0" class="team-empty">
                  No team members found for this patient.
                </div>
                <div v-else class="groups-list">
                  <div
                    v-for="member in getPatientTeamMembers(contact)"
                    :key="member.id || member.email || member.name"
                    class="group-option"
                  >
                    <div class="group-info">
                      <div class="group-name">{{ member.name }}</div>
                      <div class="group-details">
                        <span class="patient-name">{{ member.groupName }}</span>
                      </div>
                    </div>
                    <button type="button" class="team-add-single-btn" @click.stop="toggleContact(member)">
                      {{ isContactSelected(member) ? 'Added' : 'Add' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Healthcare Professionals -->
        <div 
          v-if="filteredContacts.healthcare.length > 0" 
          class="contact-category"
        >
          <div class="category-header" @click="toggleCategory('healthcare')">
            <font-awesome-icon 
              :icon="['fas', categoryExpanded.healthcare ? 'caret-down' : 'caret-right']" 
              class="expand-icon"
            />
            <font-awesome-icon :icon="['fas', 'user-md']" class="category-icon" />
            <span class="category-title">Healthcare Professionals</span>
            <span class="category-count">({{ filteredContacts.healthcare.length }})</span>
          </div>
          <div v-show="categoryExpanded.healthcare" class="contact-list">
            <div 
              v-for="contact in filteredContacts.healthcare" 
              :key="contact.id"
              class="contact-item"
            >
              <!-- Main contact row -->
              <div class="contact-main-row">
                <!-- Checkbox for selection -->
                <div class="contact-checkbox" @click="toggleContact(contact)">
                  <span v-if="isContactSelected(contact)" class="checked-text">✓</span>
                  <div 
                    v-else
                    class="unchecked-box"
                  ></div>
                </div>
                
                <!-- Contact info -->
                <div class="contact-info">
                  <div class="contact-avatar">
                    <font-awesome-icon :icon="getContactIcon(contact.accountType)" />
                  </div>
                  <div class="contact-details">
                    <div class="contact-name">{{ contact.name }}</div>
                    <div class="contact-role">{{ getDisplayRole(contact.accountType) }}</div>
                  </div>
                </div>
                
                <!-- Status or Group Indicator -->
                <div class="contact-status">
                  <button
                    v-if="isPatientContact(contact)"
                    type="button"
                    class="group-indicator-btn"
                    @click.stop="toggleContactGroups(contact)"
                  >
                    <font-awesome-icon
                      :icon="['fas', showContactGroups[contact.id] ? 'caret-down' : 'caret-right']"
                      class="team-caret-icon"
                    />
                  </button>
                </div>
              </div>
              
              <div v-if="isPatientContact(contact) && showContactGroups[contact.id]" class="contact-groups-dropdown">
                <div class="team-panel-header">
                  <button type="button" class="add-team-btn" @click.stop="addPatientTeam(contact)">
                    Add Entire Team
                  </button>
                  <span class="team-count">
                    {{ getPatientTeamMembers(contact).length }} team member{{ getPatientTeamMembers(contact).length !== 1 ? 's' : '' }}
                  </span>
                </div>
                <div v-if="getPatientTeamMembers(contact).length === 0" class="team-empty">
                  No team members found for this patient.
                </div>
                <div v-else class="groups-list">
                  <div
                    v-for="member in getPatientTeamMembers(contact)"
                    :key="member.id || member.email || member.name"
                    class="group-option"
                  >
                    <div class="group-info">
                      <div class="group-name">{{ member.name }}</div>
                      <div class="group-details">
                        <span class="patient-name">{{ member.groupName }}</span>
                      </div>
                    </div>
                    <button type="button" class="team-add-single-btn" @click.stop="toggleContact(member)">
                      {{ isContactSelected(member) ? 'Added' : 'Add' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Patients (if user can message them) -->
        <div 
          v-if="filteredContacts.patients.length > 0" 
          class="contact-category"
        >
          <div class="category-header" @click="toggleCategory('patients')">
            <font-awesome-icon 
              :icon="['fas', categoryExpanded.patients ? 'caret-down' : 'caret-right']" 
              class="expand-icon"
            />
            <font-awesome-icon :icon="['fas', 'user-md']" class="category-icon" />
            <span class="category-title">Patients</span>
            <span class="category-count">({{ filteredContacts.patients.length }})</span>
          </div>
          <div v-show="categoryExpanded.patients" class="contact-list">
            <div 
              v-for="contact in filteredContacts.patients" 
              :key="contact.id"
              class="contact-item"
            >
              <!-- Main contact row -->
              <div class="contact-main-row">
                <!-- Checkbox for selection -->
                <div class="contact-checkbox" @click="toggleContact(contact)">
                  <span v-if="isContactSelected(contact)" class="checked-text">✓</span>
                  <div 
                    v-else
                    class="unchecked-box"
                  ></div>
                </div>
                
                <!-- Contact info -->
                <div class="contact-info">
                  <div class="contact-avatar">
                    <font-awesome-icon :icon="['fas', 'user-md']" />
                  </div>
                  <div class="contact-details">
                    <div class="contact-name">{{ contact.name }}</div>
                    <div class="contact-role">Patient</div>
                    <span v-if="contact.assignedProvider" class="contact-assignment">• Assigned to {{ contact.assignedProvider }}</span>
                  </div>
                </div>
                
                <!-- Status or Group Indicator -->
                <div class="contact-status">
                  <button
                    v-if="isPatientContact(contact)"
                    type="button"
                    class="group-indicator-btn"
                    @click.stop="toggleContactGroups(contact)"
                  >
                    <font-awesome-icon
                      :icon="['fas', showContactGroups[contact.id] ? 'caret-down' : 'caret-right']"
                      class="team-caret-icon"
                    />
                  </button>
                </div>
              </div>
              
              <div v-if="isPatientContact(contact) && showContactGroups[contact.id]" class="contact-groups-dropdown">
                <div class="team-panel-header">
                  <button type="button" class="add-team-btn" @click.stop="addPatientTeam(contact)">
                    Add Entire Team
                  </button>
                  <span class="team-count">
                    {{ getPatientTeamMembers(contact).length }} team member{{ getPatientTeamMembers(contact).length !== 1 ? 's' : '' }}
                  </span>
                </div>
                <div v-if="getPatientTeamMembers(contact).length === 0" class="team-empty">
                  No team members found for this patient.
                </div>
                <div v-else class="groups-list">
                  <div
                    v-for="member in getPatientTeamMembers(contact)"
                    :key="member.id || member.email || member.name"
                    class="group-option"
                  >
                    <div class="group-info">
                      <div class="group-name">{{ member.name }}</div>
                      <div class="group-details">
                        <span class="patient-name">{{ member.groupName }}</span>
                      </div>
                    </div>
                    <button type="button" class="team-add-single-btn" @click.stop="toggleContact(member)">
                      {{ isContactSelected(member) ? 'Added' : 'Add' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- External Contacts -->
        <div 
          v-if="filteredContacts.external.length > 0" 
          class="contact-category"
        >
          <div class="category-header" @click="toggleCategory('external')">
            <font-awesome-icon 
              :icon="['fas', categoryExpanded.external ? 'caret-down' : 'caret-right']" 
              class="expand-icon"
            />
            <font-awesome-icon :icon="['fas', 'envelope']" class="category-icon" />
            <span class="category-title">External Contacts</span>
            <span class="category-count">({{ filteredContacts.external.length }})</span>
          </div>
          <div v-show="categoryExpanded.external" class="contact-list">
            <div 
              v-for="contact in filteredContacts.external" 
              :key="contact.id"
              class="contact-item"
            >
              <!-- Main contact row -->
              <div class="contact-main-row">
                <!-- Checkbox for selection -->
                <div class="contact-checkbox" @click="toggleContact(contact)">
                  <span v-if="isContactSelected(contact)" class="checked-text">✓</span>
                  <div 
                    v-else
                    class="unchecked-box"
                  ></div>
                </div>
                
                <!-- Contact info -->
                <div class="contact-info">
                  <div class="contact-avatar">
                    <font-awesome-icon :icon="['fas', 'envelope']" />
                  </div>
                  <div class="contact-details">
                    <div class="contact-name">{{ contact.name }}</div>
                    <div class="contact-role">{{ contact.email }}</div>
                  </div>
                </div>
                
                <!-- Status or Group Indicator -->
                <div class="contact-status">
                  <button
                    v-if="isPatientContact(contact)"
                    type="button"
                    class="group-indicator-btn"
                    @click.stop="toggleContactGroups(contact)"
                  >
                    <font-awesome-icon
                      :icon="['fas', showContactGroups[contact.id] ? 'caret-down' : 'caret-right']"
                      class="team-caret-icon"
                    />
                  </button>
                </div>
              </div>
                
                <div v-if="isPatientContact(contact) && showContactGroups[contact.id]" class="contact-groups-dropdown">
                  <div class="team-panel-header">
                    <button type="button" class="add-team-btn" @click.stop="addPatientTeam(contact)">
                      Add Entire Team
                    </button>
                    <span class="team-count">
                      {{ getPatientTeamMembers(contact).length }} team member{{ getPatientTeamMembers(contact).length !== 1 ? 's' : '' }}
                    </span>
                  </div>
                  <div v-if="getPatientTeamMembers(contact).length === 0" class="team-empty">
                    No team members found for this patient.
                  </div>
                  <div v-else class="groups-list">
                    <div
                      v-for="member in getPatientTeamMembers(contact)"
                      :key="member.id || member.email || member.name"
                      class="group-option"
                    >
                      <div class="group-info">
                        <div class="group-name">{{ member.name }}</div>
                        <div class="group-details">
                          <span class="patient-name">{{ member.groupName }}</span>
                        </div>
                      </div>
                      <button type="button" class="team-add-single-btn" @click.stop="toggleContact(member)">
                        {{ isContactSelected(member) ? 'Added' : 'Add' }}
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="!hasAnyContacts" class="empty-state">
          <font-awesome-icon :icon="['fas', 'user-md']" class="empty-icon" />
          <h4>No contacts available</h4>
          <p>You don't have permission to message any contacts, or no contacts match your search.</p>
        </div>
      </div>
    </div>



    <!-- Action Buttons -->
    <div class="contact-actions">
      <button @click="$emit('cancel')" class="cancel-btn">
        Cancel
      </button>
      <button 
        @click="confirmSelection" 
        :disabled="selectedContacts.length === 0 && selectedPatientGroups.size === 0"
        class="confirm-btn"
        :title="`Contacts: ${selectedContacts.length}, Groups: ${selectedPatientGroups.size}, Total: ${getTotalSelectedCount}`"
      >
        Add {{ getTotalSelectedCount }} Recipient{{ getTotalSelectedCount !== 1 ? 's' : '' }}
      </button>
    </div>
  </div>
</template>

<script>
import { CURR_API } from "@/constants/apis";
import axios from "axios";
import { mapGetters } from "vuex";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { sessionActorFrom } from "@/utils/sessionIdentity";

export default {
  name: "ContactSelectionList",
  
  data() {
    return {
      isLoading: false,
      searchTerm: '',
      selectedContacts: [],
      allContacts: [],
      accountType: '', // Store the fetched account type
      patientGroups: [],
      patientGroupsMap: {}, // Map of patient ID to their groups
      showPatientGroupDropdowns: {}, // Track which patient dropdowns are open
      showContactGroups: {}, // Track which patient groups are expanded
      selectedPatientGroups: new Set(), // Set of selected patient groups
      
      // Category expansion state
      categoryExpanded: {
        assignedTeamMembers: true,
        administrators: true,
        healthcare: true,
        patients: true,
        external: false,
        patient_groups: true
      }
    };
  },
  
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    
    currentUser() {
      const actor = sessionActorFrom(this.allData, useSessionSelfStore().profile);
      return {
        id: actor.unique_ID,
        accountNumber: actor.accountnumber,
        subAccountNumber: actor.unique_ID,
        accountType: this.normalizeAccountType(this.accountType),
        name: actor.displayName,
        email: actor.email,
      };
    },
    
    // Account hierarchy for permission checking
    accountHierarchy() {
      return {
        'agency': ['nursing-agency', 'home-healthcare-agency', 'care-management-agency'],
        'manager': ['administrator', 'nurse-manager'],
        'healthcare': ['registered-nurse', 'licensed-practical-nurse', 'home-health-aide', 'provider'],
        'patient': ['patients']
      };
    },
    
    userPermissionLevel() {
      for (const [level, types] of Object.entries(this.accountHierarchy)) {
        if (types.includes(this.currentUser.accountType)) {
          return level;
        }
      }
      return 'patient';
    },
    
    filteredContacts() {
      const filtered = {
        assignedTeamMembers: [],
        administrators: [],
        healthcare: [],
        patients: [],
        external: []
      };
      
      const searchLower = this.searchTerm.toLowerCase();
      
      this.allContacts.forEach(contact => {
        // Apply search filter
        if (searchLower && !contact.name.toLowerCase().includes(searchLower) && 
            !contact.email?.toLowerCase().includes(searchLower)) {
          return;
        }
        
        // Apply permission filter
        if (!this.canMessageContact(contact)) {
          return;
        }
        
        if (contact.category === 'assignedTeamMembers' || contact.isAssignedToPatient) {
          filtered.assignedTeamMembers.push(contact);
          return; 
        }
        if (contact.category && filtered[contact.category]) {
          filtered[contact.category].push(contact);
          return;
        }
        
        // Categorize contact
        if (contact.isExternal) {
          filtered.external.push(contact);
        } else if (this.accountHierarchy.agency.includes(contact.accountType) ||
                   this.accountHierarchy.manager.includes(contact.accountType)) {
          filtered.administrators.push(contact);
        } else if (this.accountHierarchy.healthcare.includes(contact.accountType)) {
          filtered.healthcare.push(contact);
        } else if (this.accountHierarchy.patient.includes(contact.accountType)) {
          filtered.patients.push(contact);
        }
      });
      
      // Sort each category
      Object.keys(filtered).forEach(category => {
        filtered[category].sort((a, b) => a.name.localeCompare(b.name));
      });
      
      return filtered;
    },
    
    hasAnyContacts() {
      return Object.values(this.filteredContacts).some(category => category.length > 0);
    },
    
    getTotalSelectedCount() {
      return this.selectedContacts.length + this.selectedPatientGroups.size;
    }
  },
  
  async mounted() {
    // Resolve caller identity then load server-filtered messaging contacts.
    await this.fetchAccountType();
    await this.loadContacts();
  },
  
  methods: {
    normalizeApiPayload(data) {
      if (!data || typeof data !== "object") return {};
      if (Object.prototype.hasOwnProperty.call(data, "status") || Object.prototype.hasOwnProperty.call(data, "data")) {
        return data;
      }
      const keys = Object.keys(data);
      if (keys.length === 1 && data[keys[0]] && typeof data[keys[0]] === "object") {
        return data[keys[0]];
      }
      return data;
    },

    async fetchAccountType() {
      try {
        const content = {
          Account: {
            API_Meth: "GetAccountType",
            accountnumber: this.currentUser.accountNumber,
            subaccountnumber: this.currentUser.subAccountNumber,
          },
        };
        
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });

        if (response.data && response.data.providerAssignment) {
          this.accountType = response.data.providerAssignment.accounttype;
        }
      } catch (error) {
        console.error('Error fetching account type:', error);
        this.accountType = '';
      }
    },
    
    async loadContacts() {
      this.isLoading = true;
      
      try {
        const contacts = await this.fetchContactsByPermission();
        this.allContacts = Array.isArray(contacts) ? contacts : [];
        // Recipient visibility is now determined server-side from proto groups/policies.
        this.patientGroups = [];
        this.patientGroupsMap = {};
        await this.fetchGroupsForAllPatients();
      } catch (error) {
        console.error('Error loading contacts:', error);
        this.allContacts = [];
      } finally {
        this.isLoading = false;
      }
    },
    
    async fetchContactsByPermission() {
      try {
        const content = {
          Messages: {
            API_Meth: "GetMessageContactsForUser",
            accountnumber: this.currentUser.accountNumber,
            requester_unique_ID: this.currentUser.subAccountNumber,
          },
        };
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(response.data);
        if (!payload || !String(payload.status || '').toLowerCase().includes('success')) {
          return [];
        }
        const rows = Array.isArray(payload.data) ? payload.data : [];
        return rows.map((row) => ({
          id: row.id || row.subAccountNumber || row.unique_ID,
          name: (row.name || '').trim(),
          email: row.email || '',
          accountType: this.normalizeAccountType(row.accountType || row.account_type || ''),
          subAccountNumber: row.subAccountNumber || row.unique_ID || row.id,
          agency: 'PACMNY',
          status: 'active',
          isExternal: !!row.isExternal,
          isAssignedToPatient: !!row.isAssignedToPatient,
          category: row.category || null,
        }));
      } catch (error) {
        console.error('Error fetching scoped message contacts:', error);
        return [];
      }
    },
    
    async fetchAllPatients() {
      const content = {
        Account: {
          API_Meth: "GetAccountType",
          accountnumber: this.currentUser.accountNumber,
          subaccountnumber: this.currentUser.subAccountNumber,
        },
      };
      
      try {
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });

        if (response.data && response.data.providerAssignment && response.data.providerAssignment.records) {
          const patients = response.data.providerAssignment.records.map(patient => this.mapPatientData(patient));
          return patients;
        }
      } catch (error) {
        console.error('Error fetching patients:', error);
      }
      
      return [];
    },
    
    async fetchAllNurses() {
      const content = {
        NursyInfo: {
          API_Meth: "GetAccountNurse",
          accountnumber: this.currentUser.accountNumber,
          action: "getall"
        },
      };
      
      try {
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        
        if (response.data && response.data.records) {
          return response.data.records.map(nurse => this.mapHealthcareData(nurse));
        }
      } catch (error) {
        console.error('Error fetching nurses:', error);
      }
      
      return [];
    },
    
    async fetchAssignedPatients() {
      // For healthcare professionals, use the same GetAccountType method as it returns assigned patients
      const content = {
        Account: {
          API_Meth: "GetAccountType",
          accountnumber: this.currentUser.accountNumber,
          subaccountnumber: this.currentUser.subAccountNumber,
        },
      };
      
      try {
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        
        if (response.data && response.data.providerAssignment && response.data.providerAssignment.records) {
          return response.data.providerAssignment.records.map(patient => this.mapPatientData(patient));
        }
      } catch (error) {
        console.error('Error fetching assigned patients:', error);
      }
      
      return [];
    },
    
    async fetchHealthcareColleagues() {
      const content = {
        NursyInfo: {
          API_Meth: "GetAccountNurse",
          accountnumber: this.currentUser.accountNumber,
          action: "getall"
        },
      };
      
      try {
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        
        if (response.data && response.data.records) {
          return response.data.records
            .filter(nurse => nurse.subaccountnumber !== this.currentUser.subAccountNumber)
            .map(nurse => this.mapHealthcareData(nurse));
        }
      } catch (error) {
        console.error('Error fetching healthcare colleagues:', error);
      }
      
      return [];
    },
    
    async fetchAccountProviders() {
      const content = {
        Provider: {
          API_Meth: "GetAccountProviders",
          accountnumber: this.currentUser.accountNumber,
        },
      };
      
      try {
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });

        if (response.data && response.data.provider && Array.isArray(response.data.provider)) {
          const mappedProviders = response.data.provider
            .filter(provider => {
              // Filter out current user by checking both subaccountnumber and providerid
              const providerSubAccount = provider.subaccountnumber || provider.providerid;
              return providerSubAccount !== this.currentUser.subAccountNumber;
            })
            .map(provider => this.mapHealthcareData(provider));
          return mappedProviders;
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
      }
      
      return [];
    },
    
    async fetchAccountUsers() {
      const content = {
        Account: {
          API_Meth: "GetAccountUsers",
          accountnumber: this.currentUser.accountNumber,
        },
      };
      
      try {
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });

        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          return response.data.data
            .filter(user => user.subaccountnumber !== this.currentUser.subAccountNumber)
            .map(user => this.mapContactData(user));
        }
      } catch (error) {
        console.error('Error fetching account users:', error);
      }
      
      return [];
    },
    
    async fetchAssignedProviders() {
      // For patients, we'll use a simpler approach for now
      // This would need a specific API endpoint to get providers assigned to a patient
      return [];
    },
    
    async fetchAssignedTeamMembers() {
      // Fetch nurses and providers from the same picklist used for assignments
      const contacts = [];
      
      try {
        const nursesContent = {
          NursyInfo: {
            API_Meth: "GetAccountNurse",
            action: "GetNursesAssigned",
            patientid: this.currentUser.subAccountNumber,
            accountnumber: this.currentUser.accountNumber,
          },
        };
        
        const nursesResponse = await axios.post(CURR_API + "/tswebhook.php", nursesContent, {
          headers: { "Content-Type": "application/json;" },
        });
        
        if (nursesResponse.data && nursesResponse.data.nurseassignmentInfo && Array.isArray(nursesResponse.data.nurseassignmentInfo)) {
          nursesResponse.data.nurseassignmentInfo.forEach(nurse => {
            const uniqueId = nurse.providerID;
            if (uniqueId && nurse.email) {
              contacts.push({
                id: uniqueId,
                name: `${nurse.firstname || ''} ${nurse.lastname || ''}`.trim(),
                email: nurse.email || '',
                accountType: 'registered-nurse',
                subAccountNumber: uniqueId,
                status: 'active',
                isExternal: false,
                isAssignedToPatient: true
              });
            }
          });
        }
        
        const providersContent = {
          Provider: {
            API_Meth: "GetAccountProviders",
            accountnumber: this.currentUser.accountNumber,
          },
        };
        
        const providersResponse = await axios.post(CURR_API + "/tswebhook.php", providersContent, {
          headers: { "Content-Type": "application/json;" },
        });
        
        if (providersResponse.data && providersResponse.data.provider && Array.isArray(providersResponse.data.provider)) {
          const assignedProvidersContent = {
            Provider: {
              API_Meth: "GetAllProviders",
              patientid: this.currentUser.subAccountNumber,
              accountnumber: this.currentUser.accountNumber,
            },
          };
          
          const assignedProvidersResponse = await axios.post(CURR_API + "/tswebhook.php", assignedProvidersContent, {
            headers: { "Content-Type": "application/json;" },
          });
         
          const assignedProviderIds = new Set();
          if (assignedProvidersResponse.data && assignedProvidersResponse.data.provider && Array.isArray(assignedProvidersResponse.data.provider)) {
            assignedProvidersResponse.data.provider.forEach(provider => {
              const uniqueId = provider.subaccountnumber || provider.npinumber;
              if (uniqueId) {
                assignedProviderIds.add(uniqueId);
              }
            });
          }
          
          providersResponse.data.provider.forEach(provider => {
            const uniqueId = provider.subaccountnumber || provider.npinumber;
            if (uniqueId && provider.email && assignedProviderIds.has(uniqueId)) {
              contacts.push({
                id: uniqueId,
                name: `${provider.firstname || ''} ${provider.lastname || ''}`.trim(),
                email: provider.email || '',
                accountType: 'provider',
                subAccountNumber: uniqueId,
                status: 'active',
                isExternal: false,
                isAssignedToPatient: true
              });
            }
          });
        }
        
        return contacts;
      } catch (error) {
        console.error('Error fetching assigned team members:', error);
        return [];
      }
    },
    
    async fetchRegularProviderContacts() {
      const contacts = [];
      
      try {
        const [assignedPatients, nurses, providers, administrators] = await Promise.all([
          this.fetchAssignedPatients(),
          this.fetchHealthcareColleagues(),
          this.fetchAccountProviders(),
          this.fetchAccountUsers()
        ]);
        
        contacts.push(...assignedPatients, ...nurses, ...providers, ...administrators);
        
        return contacts;
      } catch (error) {
        console.error('Error fetching regular provider contacts:', error);
        return [];
      }
    },
    
    // Fallback method using the existing SearchContacts API
    async fetchContactsViaSearch() {
      const content = {
        Messages: {
          API_Meth: "SearchContacts",
          accountnumber: this.currentUser.accountNumber,
          searchterm: "", // Empty search to get all contacts
          searchtype: "SearchContacts",
          sbaccnt: this.currentUser.subAccountNumber,
        },
      };
      
      try {
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });

        if (response.data && Array.isArray(response.data)) {
          return response.data.map(contact => ({
            id: contact.subaccountnumber || contact.id,
            name: `${contact.Firstname || contact.firstname || ''} ${contact.Lastname || contact.lastname || ''}`.trim(),
            email: contact.email || '',
            accountType: this.normalizeAccountType(contact.accounttype || 'unknown'),
            subAccountNumber: contact.subaccountnumber,
            agency: 'PACMNY',
            status: 'active',
            isExternal: false
          }));
        }
      } catch (error) {
        console.error('Error in fallback search:', error);
      }
      
      return [];
    },
    
    // Data mapping helpers
    mapContactData(contact) {
      return {
        id: contact.subaccountnumber || contact.id,
        name: `${contact.firstname || contact.Firstname} ${contact.lastname || contact.Lastname}`,
        email: contact.email,
        accountType: this.normalizeAccountType(contact.accounttype || contact.account_type),
        subAccountNumber: contact.subaccountnumber,
        agency: contact.agency || 'PACMNY',
        status: contact.status || 'active',
        isExternal: false
      };
    },
    
    mapPatientData(patient) {
      return {
        id: patient.patient_id || patient.psersonalID,
        name: `${patient.first_name || ''} ${patient.lastname || ''}`.trim(),
        email: patient.email || '',
        accountType: 'patients',
        subAccountNumber: patient.patient_id || patient.psersonalID,
        assignedProvider: patient.assigned_to || patient.assignedTo || '',
        status: patient.active === 'Active' ? 'active' : patient.active === 'Inactive' ? 'inactive' : 'active',
        isExternal: false
      };
    },
    
    mapHealthcareData(healthcare) {
      // Use the correct field names from the API response
      const firstName = healthcare.firstname || '';
      const lastName = healthcare.lastname || '';
      const email = healthcare.email || '';
      const subAccountNumber = healthcare.subaccountnumber || healthcare.providerid || '';
      const status = healthcare.status || 'active';
      
      // For providers from GetAccountProviders, we know they are providers
      // For nurses from GetAccountNurse, they are nurses  
      // Default to 'provider' for healthcare data
      const accountType = healthcare.accounttype || healthcare.role || 'provider';
      
      const mapped = {
        id: subAccountNumber,
        name: `${firstName} ${lastName}`.trim(),
        email: email,
        accountType: this.normalizeAccountType(accountType),
        subAccountNumber: subAccountNumber,
        agency: healthcare.agency || 'PACMNY',
        status: status.toLowerCase() === 'active' ? 'active' : status.toLowerCase(),
        isExternal: false
      };
      return mapped;
    },
    
    normalizeAccountType(type) {
      if (!type) return 'unknown';
      
      const typeMap = {
        'Provider': 'provider',
        'Registered Nurse': 'registered-nurse',
        'Licensed Practical Nurse': 'licensed-practical-nurse',
        'Home Health Aide': 'home-health-aide',
        'Administrator': 'administrator',
        'Nurse Manager': 'nurse-manager',
        'Nursing Agency': 'nursing-agency',
        'Home Healthcare Agency': 'home-healthcare-agency',
        'Care Management Agency': 'care-management-agency',
        'Patient': 'patients'
      };
      
      return typeMap[type] || type.toLowerCase().replace(/\s+/g, '-');
    },
    
    // Permission checking
    canMessageContact(contact) {
      // Users can always message contacts that are returned by their permission-based queries
      // This method can be used for additional fine-grained permission checks if needed
      return true;
    },
    
    // UI interaction methods
    toggleCategory(category) {
      this.categoryExpanded[category] = !this.categoryExpanded[category];
    },
    
    toggleContact(contact) {
      if (!this.canMessageContact(contact)) return;
      
      const index = this.selectedContacts.findIndex(c => c.id === contact.id);
      if (index > -1) {
        this.selectedContacts.splice(index, 1);
      } else {
        this.selectedContacts.push(contact);
        // If this is a patient, fetch their groups
        this.onPatientSelected(contact);
      }
    },
    
    isContactSelected(contact) {
      return this.selectedContacts.some(c => c.id === contact.id);
    },
    
    removeContact(contact) {
      const index = this.selectedContacts.findIndex(c => c.id === contact.id);
      if (index > -1) {
        this.selectedContacts.splice(index, 1);
      }
    },

    removeGroup(groupName) {
      this.selectedPatientGroups.delete(groupName);
    },
    
    clearAllSelections() {
      this.selectedContacts = [];
      this.selectedPatientGroups.clear();
    },
    
    filterContacts() {
      // Filtering is handled by computed property
    },
    
    clearSearch() {
      this.searchTerm = '';
    },
    
    confirmSelection() {
      // Combine selected contacts and selected patient groups
      const allSelections = [...this.selectedContacts];
      
      // Add selected patient groups as group selections
      this.selectedPatientGroups.forEach(groupName => {
        // Find the group details from patientGroupsMap
        for (const patientId in this.patientGroupsMap) {
          const groups = this.patientGroupsMap[patientId];
          const group = groups.find(g => g.group_name === groupName);
          if (group) {
            allSelections.push({
              id: `group_${groupName}`,
              name: `${group.group_name} Care Team`,
              email: `Group: ${group.patient_name}`,
              accountType: 'patient-group',
              isGroup: true,
              groupData: group
            });
            break;
          }
        }
      });
      
      this.$emit('contacts-selected', allSelections);
    },
    
    async fetchGroupsForAllPatients() {
      try {
        // Get all patient contacts
        const patientContacts = this.allContacts.filter(contact => 
          contact.accountType === 'patient' || contact.accountType === 'patients'
        );

        // Fetch groups for each patient
        for (const patient of patientContacts) {
          const patientId = patient.subAccountNumber || patient.id;
          if (patientId && !this.patientGroupsMap[patientId]) {
            const groups = await this.fetchPatientGroupsForPatient(patientId);
            if (typeof this.$set === 'function') {
              this.$set(this.patientGroupsMap, patientId, groups);
            } else {
              this.patientGroupsMap = { ...this.patientGroupsMap, [patientId]: groups };
            }
          }
        }
      } catch (error) {
        console.error('❌ Error fetching groups for all patients:', error);
      }
    },
    
    async fetchPatientGroups() {
      try {
        const currentUser = this.allData.patient;
        if (!currentUser || !currentUser.externId) {
          return;
        }
        
        const formData = new FormData();
        formData.append("API_Meth", "GetPatientGroups");
        formData.append("patient_id", currentUser.externId);
        
        // Check if current user is a provider
        // GetAccountType answers main's words ('Provider'); folded so the
        // words and the API's slug both match.
        const ownType = String(this.accountType || '').trim().toLowerCase();
        const isProvider = ownType === 'provider' || ownType === 'nurse-manager' || ownType === 'administrator';
        formData.append("is_provider", isProvider);
        if (isProvider) {
          formData.append("provider_id", currentUser.externId);
        }
        
        const response = await axios.post(CURR_API + "/tswebhook.php", formData);
        
        if (response.data && response.data.status === "success") {
          this.patientGroups = response.data.groups || [];
        } else {
          this.patientGroups = [];
        }
      } catch (error) {
        console.error('Error fetching patient groups:', error);
        this.patientGroups = [];
      }
    },
    
    async fetchPatientGroupsForPatient(patientId) {
      try {
        const content = {
          Messages: {
            API_Meth: "GetPatientTeamForMessaging",
            accountnumber: this.currentUser.accountNumber,
            patient_unique_ID: patientId,
          },
        };
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(response.data);
        if (!payload || !String(payload.status || '').toLowerCase().includes('success')) {
          return [];
        }
        return Array.isArray(payload.data) ? payload.data : [];
      } catch (error) {
        console.error('Error fetching patient team groups:', error);
        return [];
      }
    },
    
    async onPatientSelected(contact) {
      // When a patient is selected, fetch their groups
      if (this.isPatientContact(contact)) {
        const patientId = this.getPatientId(contact);
        if (patientId && !this.patientGroupsMap[patientId]) {
          const groups = await this.fetchPatientGroupsForPatient(patientId);
          if (typeof this.$set === 'function') {
            this.$set(this.patientGroupsMap, patientId, groups);
          } else {
            this.patientGroupsMap = { ...this.patientGroupsMap, [patientId]: groups };
          }
        }
      }
    },
    
    togglePatientGroupDropdown(patientId) {
      this.showPatientGroupDropdowns[patientId] = !this.showPatientGroupDropdowns[patientId];
    },
    
    selectPatientGroup(group) {
      // Create a group chat with all members of this patient group
      const groupParticipants = [];
      
      // Add the patient themselves
      const currentUser = this.allData.patient;
      if (currentUser) {
        groupParticipants.push({
          id: currentUser.externId,
          name: `${currentUser.fname} ${currentUser.lname}`,
          email: currentUser.email || '',
          accountType: 'patient',
          isGroup: true,
          groupName: group.group_name
        });
      }
      
      // Add all group members (providers)
      group.members.forEach(member => {
        // We need to find the actual provider ID from their email/name
        // For now, we'll use a placeholder approach
        groupParticipants.push({
          id: member.email, // Temporary - we'll need to map this properly
          name: member.name,
          email: member.email,
          accountType: 'provider',
          isGroup: true,
          groupName: group.group_name
        });
      });
      
      // Emit the group selection
      this.$emit('group-selected', {
        groupName: group.group_name,
        participants: groupParticipants,
        isPatientGroup: true
      });
    },
    
    getPatientName(patientId) {
      // First try to find the patient in our contacts
      const patient = this.allContacts.find(contact => 
        contact.subAccountNumber === patientId || contact.id === patientId
      );
      
      if (patient) {
        return patient.name;
      }
      
      // If current user is the patient
      if (this.allData.patient && this.allData.patient.externId === patientId) {
        return `${this.allData.patient.fname} ${this.allData.patient.lname}`;
      }
      
      // Fallback - we'll enhance this with a lookup if needed
      return `Patient ${patientId}`;
    },
    
    // Utility methods
    getContactIcon(accountType) {
      const iconMap = {
        'administrator': ['fa', 'user-md'],
        'nurse-manager': ['fa', 'user-md'],
        'provider': ['fa', 'user-md'],
        'registered-nurse': ['fa', 'user-md'],
        'licensed-practical-nurse': ['fa', 'user-md'],
        'home-health-aide': ['fa', 'user-md'],
        'nursing-agency': ['fa', 'building'],
        'home-healthcare-agency': ['fa', 'building'],
        'care-management-agency': ['fa', 'building'],
        'patients': ['fa', 'user-md'],
        'external': ['fa', 'envelope']
      };
      
      return iconMap[accountType] || ['fa', 'user-md'];
    },
    
    getDisplayRole(accountType) {
      const roleMap = {
        'administrator': 'Administrator',
        'nurse-manager': 'Nurse Manager',
        'provider': 'Provider',
        'registered-nurse': 'Registered Nurse',
        'licensed-practical-nurse': 'Licensed Practical Nurse',
        'home-health-aide': 'Home Health Aide',
        'nursing-agency': 'Nursing Agency',
        'home-healthcare-agency': 'Home Healthcare Agency',
        'care-management-agency': 'Care Management Agency',
        'patients': 'Patient'
      };
      
      return roleMap[accountType] || accountType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    async toggleContactGroups(contact) {
      if (!this.isPatientContact(contact)) return;
      const patientId = this.getPatientId(contact);
      if (patientId && !this.patientGroupsMap[patientId]) {
        const groups = await this.fetchPatientGroupsForPatient(patientId);
        if (typeof this.$set === 'function') {
          this.$set(this.patientGroupsMap, patientId, groups);
        } else {
          this.patientGroupsMap = { ...this.patientGroupsMap, [patientId]: groups };
        }
      }
      const nextValue = !this.showContactGroups[contact.id];
      if (typeof this.$set === 'function') {
        this.$set(this.showContactGroups, contact.id, nextValue);
      } else {
        this.showContactGroups = { ...this.showContactGroups, [contact.id]: nextValue };
      }
    },

    hasPatientGroups(contact) {
      const patientId = contact.subAccountNumber || contact.id;
      const hasGroups = (contact.accountType === 'patient' || contact.accountType === 'patients') && this.patientGroupsMap[patientId] && this.patientGroupsMap[patientId].length > 0;
      return hasGroups;
    },

    getPatientGroups(contact) {
      const patientId = contact.subAccountNumber || contact.id;
      return this.patientGroupsMap[patientId] || [];
    },

    isGroupSelected(group) {
      return this.selectedPatientGroups.has(group.group_name);
    },

    toggleGroupSelection(group, contact) {
      const groupName = group.group_name;
      if (this.selectedPatientGroups.has(groupName)) {
        this.selectedPatientGroups.delete(groupName);
      } else {
        this.selectedPatientGroups.add(groupName);
      }

      // Don't emit event - handle selection internally
      // this.$emit('group-selected', {
      //   groupName: groupName,
      //   participants: [contact], // For now, just the contact itself
      //   isPatientGroup: true
      // });
    },

    isPatientContact(contact) {
      return contact && (contact.accountType === 'patient' || contact.accountType === 'patients');
    },

    getPatientId(contact) {
      return contact?.subAccountNumber || contact?.id || null;
    },

    getPatientTeamMembers(contact) {
      const groups = this.getPatientGroups(contact);
      const teamMap = new Map();

      groups.forEach((group) => {
        const groupName = group?.group_name || 'Team';
        const members = Array.isArray(group?.members) ? group.members : [];

        members.forEach((member) => {
          const rawId =
            member?.unique_ID ||
            member?.subaccountnumber ||
            member?.subAccountNumber ||
            member?.providerID ||
            member?.providerid ||
            member?.id ||
            member?.email ||
            `${groupName}-${member?.name || member?.email || 'member'}`;
          const id = String(rawId);
          const existing = teamMap.get(id);
          const memberName = (member?.name || `${member?.firstname || ''} ${member?.lastname || ''}`).trim() || 'Unknown member';
          const email = member?.email || '';
          const accountType = this.normalizeAccountType(member?.accountType || member?.accounttype || member?.role || 'provider');

          if (!existing) {
            teamMap.set(id, {
              id,
              name: memberName,
              email,
              accountType,
              subAccountNumber: id,
              isExternal: false,
              isAssignedToPatient: true,
              _groups: new Set([groupName]),
            });
            return;
          }
          existing._groups.add(groupName);
        });
      });

      return Array.from(teamMap.values())
        .map((member) => ({
          ...member,
          groupName: Array.from(member._groups).join(', '),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    addPatientTeam(contact) {
      const teamMembers = this.getPatientTeamMembers(contact);
      teamMembers.forEach((member) => {
        if (!this.selectedContacts.some((existing) => existing.id === member.id)) {
          this.selectedContacts.push(member);
        }
      });
    }
  }
};
</script>

<style scoped>
.contact-selection-list {
  display: flex;
  flex-direction: column;
  height: 600px;
  width: 450px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.svg-inline--fa {
  display: none !important;
}

.team-caret-icon {
  display: inline-block !important;
  color: #007bff;
  font-size: 16px;
}

/* Header */
.contact-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.contact-header h3 {
  margin: 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
}

.contact-stats {
  font-size: 14px;
  color: #6c757d;
}

.selected-count {
  font-weight: 500;
}

/* Search */
.contact-search {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #6c757d;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.clear-search {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s ease;
}

.clear-search:hover {
  color: #495057;
}

/* Selected Recipients Bar */
.selected-recipients-bar {
  border-bottom: 1px solid #e9ecef;
  background: #f8f9ff;
}

.selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
}

.clear-all-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.clear-all-btn:hover {
  background: rgba(220, 53, 69, 0.1);
}

.selected-recipients-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 20px 12px;
}

.selected-recipient {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #e7f3ff;
  border: 1px solid #cce7ff;
  border-radius: 8px;
  padding: 8px 12px;
  gap: 10px;
}

.recipient-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.recipient-icon {
  font-size: 14px;
  color: #667eea;
}

.recipient-name {
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.recipient-type {
  font-size: 12px;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 6px;
}

.remove-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.remove-btn:hover {
  background: rgba(220, 53, 69, 0.1);
}

.patient-groups-dropdown {
  margin-top: 8px;
  border-top: 1px solid #e9ecef;
  padding-top: 12px;
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f1f3f4;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.dropdown-header:hover {
  background: #e9ecef;
}

.dropdown-header .group-icon {
  color: #6c757d;
  font-size: 14px;
}

.dropdown-header .expand-icon {
  font-size: 12px;
  color: #6c757d;
  transition: transform 0.2s ease;
}

.dropdown-content {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  max-height: 200px; /* Adjust as needed */
  overflow-y: auto;
  position: absolute;
  z-index: 10;
  width: 100%;
}

.dropdown-content .group-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid #f1f3f4;
}

.dropdown-content .group-option:last-child {
  border-bottom: none;
}

.dropdown-content .group-option:hover {
  background: #f8f9fa;
}

.dropdown-content .group-option .group-icon {
  color: #6c757d;
  font-size: 14px;
}

.dropdown-content .group-option .group-name {
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.dropdown-content .group-option .member-count {
  font-size: 12px;
  color: #6c757d;
  margin-left: auto;
}

.dropdown-content .group-option .patient-name {
  font-weight: 500;
  color: #5e72e4;
  font-size: 13px;
}

/* Contact Categories */
.contact-categories {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #6c757d;
  gap: 12px;
}

.loading-state .fa-spin {
  font-size: 24px;
}

.contact-lists {
  flex: 1;
  overflow-y: auto;
}

/* Category Headers */
.contact-category {
  border-bottom: 1px solid #f1f3f4;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: #f8f9fa;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid #e9ecef;
}

.category-header:hover {
  background: #e9ecef;
}

.expand-icon {
  font-size: 12px;
  color: #6c757d;
  transition: transform 0.2s ease;
}

.category-icon {
  color: #667eea;
  font-size: 14px;
}

.category-title {
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.category-count {
  color: #6c757d;
  font-size: 12px;
  margin-left: auto;
}

/* Contact List */
.contact-list {
  background: white;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid #f8f9fa;
  flex-direction: column;
}

.contact-main-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.contact-item:hover {
  background: #f8f9fa;
}

.contact-item.selected {
  background: #e7f3ff;
  border-color: #cce7ff;
}

.contact-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.contact-checkbox {
  color: #6c757d;
  font-size: 14px;
  transition: color 0.2s ease;
}

.contact-checkbox .checked {
  color: #667eea;
}

.checked-text {
  color: #667eea;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.unchecked-box {
  width: 14px;
  height: 14px;
  border: 2px solid #6c757d;
  border-radius: 2px;
  background: transparent;
}

.contact-item .contact-avatar {
  display: none;
}

.contact-info {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.contact-name {
  font-weight: 500;
  color: #495057;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-details {
  font-size: 12px;
  color: #6c757d;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-role {
  font-weight: 500;
}

.contact-agency,
.contact-assignment {
  opacity: 0.8;
}

.contact-status {
  display: flex;
  align-items: center;
}



/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
}

.empty-icon {
  font-size: 48px;
  color: #dee2e6;
  margin-bottom: 16px;
}

.empty-state h4 {
  margin: 0 0 8px 0;
  color: #495057;
  font-size: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}



/* Action Buttons */
.contact-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.cancel-btn,
.confirm-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: #f8f9fa;
  border: 1px solid #ced4da;
  color: #6c757d;
}

.cancel-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.confirm-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  flex: 1;
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Scrollbar Styling */
.contact-lists::-webkit-scrollbar {
  width: 6px;
}

.contact-lists::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.contact-lists::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.contact-lists::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsive Design */
@media (max-width: 600px) {
  .contact-selection-list {
    width: calc(100vw - 60px);
    height: calc(100vh - 120px);
    max-width: none;
  }
  
  .patient-badge {
    font-size: 10px;
    padding: 3px 6px;
  }
  
  .group-name {
    font-size: 13px;
  }
  
  .group-details {
    font-size: 11px;
  }
}

/* Contact Groups Dropdown */
.contact-groups-dropdown {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  margin-top: 8px;
  width: 100%;
  clear: both;
  position: relative;
  left: 0;
  right: 0;
}

.groups-list {
  padding: 8px 0;
}

.group-option {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.group-option:hover {
  background-color: #e9ecef;
}

.group-option .group-checkbox {
  flex-shrink: 0;
}

.group-option .group-info {
  flex: 1;
  min-width: 0;
}

.group-option .group-name {
  font-weight: 500;
  color: #495057;
  font-size: 13px;
  margin-bottom: 2px;
}

.group-option .group-details {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #6c757d;
}

.group-option .member-count {
  color: #28a745;
}

.group-option .patient-name {
  color: #5e72e4;
}

/* Group Indicator Arrow */
.group-indicator-btn {
  margin-left: auto;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  cursor: pointer;
}

.group-arrow {
  color: #007bff;
  font-size: 16px;
  transition: all 0.2s ease;
  display: block;
}

.group-indicator-btn:hover .group-arrow {
  color: #0056b3 !important;
  transform: scale(1.1);
}

.team-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid #e9ecef;
}

.add-team-btn {
  background: #5e72e4;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.add-team-btn:hover {
  background: #4a5ecf;
}

.team-count {
  color: #6c757d;
  font-size: 12px;
}

.team-empty {
  padding: 12px 16px;
  color: #6c757d;
  font-size: 12px;
}

.team-add-single-btn {
  background: transparent;
  border: 1px solid #ced4da;
  border-radius: 6px;
  color: #495057;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
}

.team-add-single-btn:hover {
  background: #f1f3f5;
}



/* Contact Info Layout */
.contact-info {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: default;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.contact-info:hover {
  background-color: #f8f9fa;
}

.contact-details {
  flex: 1;
  min-width: 0;
}

.contact-name {
  font-weight: 500;
  color: #495057;
  margin-bottom: 2px;
}

.contact-role {
  font-size: 12px;
  color: #6c757d;
}
</style>
